"use client";

import React, { useState, useMemo } from "react";
import { format, isToday, isTomorrow, isYesterday, isBefore, startOfDay } from "date-fns";
import { Calendar, Clock, MapPin, Users, ExternalLink, Download, ExternalLink as ExternalLinkIcon, ChevronUp, ChevronDown } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../card.js";
import { Button } from "../button.js";
import { Badge } from "../badge.js";
import { CalendarEvent } from "../../types/calendar.js";
import { downloadCSV, objectsToCSV } from "../../lib/csv-export.js";
import { EventDetailsModal } from "./EventDetailsModal.js";
import { useToast } from "../../hooks/use-toast.js";
import { useSourceColors, useCalendarSources } from "../../lib/calendar-context.js";
import Link from "next/link";
import { formatWtcEmailCalendarSourceForDisplay } from "../../hooks/api/calendar/constants.js";

interface EventListProps {
  events: CalendarEvent[];
  isLoading: boolean;
}

export function EventList({ events, isLoading }: EventListProps) {
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [isEventModalOpen, setIsEventModalOpen] = useState(false);
  const [showPastEvents, setShowPastEvents] = useState(false);
  const { toast } = useToast();
  const sourceColors = useSourceColors();
  const calendarSources = useCalendarSources();

  const handleTogglePastEvents = () => {
    setShowPastEvents(!showPastEvents);
    // Smooth scroll to top when showing past events, or to current events when hiding
    if (!showPastEvents) {
      // When showing past events, scroll to top after a brief delay to let the content render
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    }
  };

  // Group events by date and separate past/future
  const { pastEvents, currentAndFutureEvents } = useMemo(() => {
    const today = startOfDay(new Date());
    const pastGroups: Record<string, CalendarEvent[]> = {};
    const futureGroups: Record<string, CalendarEvent[]> = {};
    
    events.forEach((event) => {
      const eventDate = new Date(event.start);
      const dateKey = format(eventDate, "yyyy-MM-dd");
      const eventDay = startOfDay(eventDate);
      
      if (isBefore(eventDay, today)) {
        // Past events
        if (!pastGroups[dateKey]) {
          pastGroups[dateKey] = [];
        }
        pastGroups[dateKey].push(event);
      } else {
        // Current and future events
        if (!futureGroups[dateKey]) {
          futureGroups[dateKey] = [];
        }
        futureGroups[dateKey].push(event);
      }
    });

    // Sort events within each group by start time
    [...Object.keys(pastGroups), ...Object.keys(futureGroups)].forEach((dateKey) => {
      if (pastGroups[dateKey]) {
        pastGroups[dateKey].sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
      }
      if (futureGroups[dateKey]) {
        futureGroups[dateKey].sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
      }
    });

    return {
      pastEvents: pastGroups,
      currentAndFutureEvents: futureGroups
    };
  }, [events]);

  const getSourceColor = (source: string) => {
    // First try to get from sourceColors (which uses CSS variables)
    let color = sourceColors[source];
    
    if (!color) {
      // Fallback based on source type
      const sourceLower = source.toLowerCase();
      if (sourceLower.includes('google') || sourceLower.includes('goog')) {
        color = "oklch(var(--calendar-google))";
      } else if (sourceLower.includes('microsoft') || sourceLower.includes('msft')) {
        color = "oklch(var(--calendar-microsoft))";
      } else if (sourceLower.includes('unified')) {
        color = "oklch(var(--calendar-unified))";
      } else if (sourceLower === "wtc-email-calendar" || sourceLower === "professional-email-calendar") {
        color = "#7c3aed";
      } else {
        color = "oklch(var(--calendar-external))";
      }
    }
    
    return {
      backgroundColor: color,
      color: "#ffffff",
      textShadow: "0 1px 2px rgba(0, 0, 0, 0.3)"
    };
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    if (isToday(date)) {
      return "Today";
    }
    if (isTomorrow(date)) {
      return "Tomorrow";
    }
    if (isYesterday(date)) {
      return "Yesterday";
    }

    return format(date, "EEEE, MMMM d");
  };

  const formatTimeRange = (start: string, end: string) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const startTime = format(startDate, "p");
    const endTime = format(endDate, "p");
    
    return `${startTime} - ${endTime}`;
  };

  const handleEventClick = (event: CalendarEvent) => {
    setSelectedEvent(event);
    setIsEventModalOpen(true);
  };

  const handleExportCSV = () => {
    if (events.length === 0) {
      toast({
        title: "No events to export",
        description: "There are no events to export at this time.",
        variant: "destructive",
      });

      return;
    }

    try {
      const csvContent = objectsToCSV(events);
      downloadCSV(csvContent, "calendar-events.csv");
      toast({
        title: "Export successful",
        description: "Calendar events have been exported to CSV.",
      });
    } catch (error) {
      toast({
        title: "Export failed",
        description: "Failed to export calendar events.",
        variant: "destructive",
      });
    }
  };

  const handleEditEvent = (event: CalendarEvent) => {
    // TODO: Implement edit functionality
    toast({
      title: "Edit feature",
      description: "Event editing will be implemented soon.",
    });
  };

  const handleDeleteEvent = (event: CalendarEvent) => {
    // TODO: Implement delete functionality
    toast({
      title: "Delete feature",
      description: "Event deletion will be implemented soon.",
    });
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Sort dates for current/future (ascending) and past (descending)
  const sortedFutureDates = Object.keys(currentAndFutureEvents).sort((a, b) => a.localeCompare(b));
  const sortedPastDates = Object.keys(pastEvents).sort((a, b) => b.localeCompare(a));
  
  const totalPastEvents = Object.values(pastEvents).reduce((sum, events) => sum + events.length, 0);

  return (
    <div className="space-y-4 touch-pan-y"
         style={{ 
           WebkitOverflowScrolling: 'touch',
           scrollBehavior: 'smooth'
         }}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0">
        <div className="flex items-center space-x-2">
          <Calendar className="h-5 w-5" />
          <h2 className="text-xl font-semibold">Event List</h2>
          <Badge variant="secondary">{events.length} events</Badge>
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          {totalPastEvents > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleTogglePastEvents}
              className="flex items-center space-x-2"
            >
              {showPastEvents ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              <span>{showPastEvents ? 'Hide' : 'Show'} Past ({totalPastEvents})</span>
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportCSV}
            className="flex items-center space-x-2"
          >
            <Download className="h-4 w-4" />
            <span>Export CSV</span>
          </Button>
        </div>
      </div>

      {/* Calendar Sources Legend */}
      {calendarSources.length > 0 && (
        <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-4 text-sm">
          <span className="text-muted-foreground whitespace-nowrap">Calendar Sources:</span>
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            {calendarSources.map((source) => (
              <div key={source.email} className="flex items-center space-x-1">
                <div 
                  className="w-3 h-3 rounded-full flex-shrink-0" 
                  style={{ backgroundColor: source.color }}
                ></div>
                <span className="text-xs truncate max-w-[120px] sm:max-w-none">
                  {formatWtcEmailCalendarSourceForDisplay(source.email)}
                </span>
                <span className="text-xs text-muted-foreground whitespace-nowrap">({source.eventCount})</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Past Events (when toggled) */}
      {showPastEvents && sortedPastDates.length > 0 && (
        <div className="space-y-4 sm:space-y-6">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <div className="h-px bg-border flex-1"></div>
            <span className="px-3 bg-background">Past Events</span>
            <div className="h-px bg-border flex-1"></div>
          </div>
          {sortedPastDates.map((dateKey) => {
            const dateEvents = pastEvents[dateKey];
            const firstEvent = dateEvents[0];
            const displayDate = formatDate(firstEvent.start);

            return (
              <Card key={`past-${dateKey}`} className="opacity-75">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base sm:text-lg text-muted-foreground">{displayDate}</CardTitle>
                </CardHeader>
                <CardContent className="p-3 sm:p-6 pt-0">
                  <div className="space-y-2 sm:space-y-3">
                    {dateEvents.map((event) => (
                      <div
                        key={event.id}
                        className="flex flex-col sm:flex-row items-start space-y-3 sm:space-y-0 sm:space-x-4 p-3 sm:p-4 border rounded-lg hover:bg-muted/50 active:bg-muted/70 transition-colors cursor-pointer opacity-80 touch-manipulation"
                        onClick={() => handleEventClick(event)}
                      >
                        <div className="flex-shrink-0 hidden sm:block">
                          <Calendar className="h-5 w-5 text-muted-foreground" />
                        </div>
                        <div className="flex-1 min-w-0 w-full">
                          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-0">
                            <div className="flex items-center space-x-2 min-w-0 flex-1">
                              <Calendar className="h-4 w-4 text-muted-foreground sm:hidden flex-shrink-0" />
                              <h3 className="font-medium text-base sm:text-lg truncate text-muted-foreground">{event.title}</h3>
                              <Link
                                href={`/unifiedtime/event/${event.id}`}
                                target="_blank"
                                className="text-muted-foreground hover:text-primary transition-colors flex-shrink-0"
                                title="Open in new tab"
                                onClick={(e: React.MouseEvent) => e.stopPropagation()}
                              >
                                <ExternalLinkIcon className="h-4 w-4" />
                              </Link>
                            </div>
                            <Badge 
                              style={getSourceColor(event.source)}
                              className="text-xs whitespace-nowrap max-w-[80px] sm:max-w-none truncate opacity-75"
                            >
                              {formatWtcEmailCalendarSourceForDisplay(event.source)}
                            </Badge>
                          </div>
                          
                          {event.description && (
                            <p className="text-muted-foreground mt-2 text-sm sm:text-base line-clamp-2 opacity-75">
                              {event.description}
                            </p>
                          )}
                          
                          <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4 mt-3 text-sm text-muted-foreground">
                            <div className="flex items-center space-x-1">
                              <Clock className="h-4 w-4 flex-shrink-0" />
                              <span className="font-medium">{formatTimeRange(event.start, event.end)}</span>
                            </div>
                            
                            {event.location && (
                              <div className="flex items-center space-x-1 min-w-0">
                                <MapPin className="h-4 w-4 flex-shrink-0" />
                                <span className="truncate">{event.location}</span>
                              </div>
                            )}
                            
                            <div className="flex items-center space-x-3 sm:space-x-4">
                              {event.attendees && event.attendees.length > 0 && (
                                <div className="flex items-center space-x-1">
                                  <Users className="h-4 w-4 flex-shrink-0" />
                                  <span className="whitespace-nowrap">{event.attendees.length} attendee{event.attendees.length !== 1 ? 's' : ''}</span>
                                </div>
                              )}
                              
                              {event.isOnlineMeeting && event.onlineMeetingUrl && (
                                <div className="flex items-center space-x-1">
                                  <ExternalLink className="h-4 w-4 flex-shrink-0" />
                                  <span className="whitespace-nowrap">Online</span>
                                </div>
                              )}
                            </div>
                          </div>
                          
                          {event.isCancelled && (
                            <Badge variant="destructive" className="mt-2 text-xs opacity-75">
                              Cancelled
                            </Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Current and Future Events */}
      {sortedFutureDates.length > 0 ? (
        <div className="space-y-4 sm:space-y-6">
          {!showPastEvents && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <div className="h-px bg-border flex-1"></div>
              <span className="px-3 bg-background">Current & Upcoming Events</span>
              <div className="h-px bg-border flex-1"></div>
            </div>
          )}
          {sortedFutureDates.map((dateKey) => {
            const dateEvents = currentAndFutureEvents[dateKey];
            const firstEvent = dateEvents[0];
            const displayDate = formatDate(firstEvent.start);

            return (
              <Card key={`future-${dateKey}`}>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base sm:text-lg">{displayDate}</CardTitle>
                </CardHeader>
                <CardContent className="p-3 sm:p-6 pt-0">
                  <div className="space-y-2 sm:space-y-3">
                    {dateEvents.map((event) => (
                      <div
                        key={event.id}
                        className="flex flex-col sm:flex-row items-start space-y-3 sm:space-y-0 sm:space-x-4 p-3 sm:p-4 border rounded-lg hover:bg-muted active:bg-muted/80 transition-colors cursor-pointer touch-manipulation"
                        onClick={() => handleEventClick(event)}
                      >
                        <div className="flex-shrink-0 hidden sm:block">
                          <Calendar className="h-5 w-5 text-muted-foreground" />
                        </div>
                        <div className="flex-1 min-w-0 w-full">
                          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-0">
                            <div className="flex items-center space-x-2 min-w-0 flex-1">
                              <Calendar className="h-4 w-4 text-muted-foreground sm:hidden flex-shrink-0" />
                              <h3 className="font-medium text-base sm:text-lg truncate">{event.title}</h3>
                              <Link
                                href={`/unifiedtime/event/${event.id}`}
                                target="_blank"
                                className="text-muted-foreground hover:text-primary transition-colors flex-shrink-0"
                                title="Open in new tab"
                                onClick={(e: React.MouseEvent) => e.stopPropagation()}
                              >
                                <ExternalLinkIcon className="h-4 w-4" />
                              </Link>
                            </div>
                            <Badge 
                              style={getSourceColor(event.source)}
                              className="text-xs whitespace-nowrap max-w-[80px] sm:max-w-none truncate"
                            >
                              {formatWtcEmailCalendarSourceForDisplay(event.source)}
                            </Badge>
                          </div>
                          
                          {event.description && (
                            <p className="text-muted-foreground mt-2 text-sm sm:text-base line-clamp-2">
                              {event.description}
                            </p>
                          )}
                          
                          <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4 mt-3 text-sm text-muted-foreground">
                            <div className="flex items-center space-x-1">
                              <Clock className="h-4 w-4 flex-shrink-0" />
                              <span className="font-medium">{formatTimeRange(event.start, event.end)}</span>
                            </div>
                            
                            {event.location && (
                              <div className="flex items-center space-x-1 min-w-0">
                                <MapPin className="h-4 w-4 flex-shrink-0" />
                                <span className="truncate">{event.location}</span>
                              </div>
                            )}
                            
                            <div className="flex items-center space-x-3 sm:space-x-4">
                              {event.attendees && event.attendees.length > 0 && (
                                <div className="flex items-center space-x-1">
                                  <Users className="h-4 w-4 flex-shrink-0" />
                                  <span className="whitespace-nowrap">{event.attendees.length} attendee{event.attendees.length !== 1 ? 's' : ''}</span>
                                </div>
                              )}
                              
                              {event.isOnlineMeeting && event.onlineMeetingUrl && (
                                <div className="flex items-center space-x-1">
                                  <ExternalLink className="h-4 w-4 flex-shrink-0" />
                                  <span className="whitespace-nowrap">Online</span>
                                </div>
                              )}
                            </div>
                          </div>
                          
                          {event.isCancelled && (
                            <Badge variant="destructive" className="mt-2 text-xs">
                              Cancelled
                            </Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card>
          <CardContent className="p-6">
            <div className="text-center py-8 text-muted-foreground">
              <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No events found.</p>
              <p className="text-sm">
                Try connecting your calendars or checking a different time period.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Event Details Modal */}
      <EventDetailsModal
        event={selectedEvent}
        isOpen={isEventModalOpen}
        onClose={() => {
          setIsEventModalOpen(false);
          setSelectedEvent(null);
        }}
        onEdit={handleEditEvent}
        onDelete={handleDeleteEvent}
      />
    </div>
  );
} 